/**
 * Extended MediaTimeDisplay that persists the remaining/elapsed time preference to localStorage.
 */

import { MediaTimeDisplay } from 'media-chrome';
import { formatTime } from 'media-chrome/utils/time.js';

const STORAGE_KEY = 'bunny_player_time_display_remaining';
const DURATION_ONLY_ATTR = 'durationonly';

class BunnyTimeDisplay extends MediaTimeDisplay {
  #preferenceRestored = false;

  static override get observedAttributes() {
    return [...super.observedAttributes, DURATION_ONLY_ATTR];
  }

  override connectedCallback() {
    super.connectedCallback();
    this.#applyTimeDisplayMode();
    this.update();

    this.addEventListener('click', this.#handleClick);
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.#handleClick);
  }

  override attributeChangedCallback(
    name: string,
    oldValue: string | null,
    newValue: string | null,
  ) {
    super.attributeChangedCallback(name, oldValue, newValue);

    if (
      (name === 'notoggle' || name === DURATION_ONLY_ATTR) &&
      oldValue !== newValue
    ) {
      this.#applyTimeDisplayMode();
      this.update();
    }
  }

  #applyTimeDisplayMode() {
    // notoggle and durationonly mean this instance should never toggle to remaining time.
    if (
      this.hasAttribute('notoggle') || this.hasAttribute(DURATION_ONLY_ATTR)
    ) {
      this.removeAttribute('remaining');
      return;
    }

    // Restore preference from localStorage once on first connect.
    if (!this.#preferenceRestored) {
      this.#preferenceRestored = true;
      this.#restorePreference();
    }
  }

  #restorePreference() {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored === 'true') {
        this.setAttribute('remaining', '');
      } else if (stored === 'false') {
        this.removeAttribute('remaining');
      }
      // If null, use default behavior (no preference stored yet)
    } catch {
      // localStorage may be unavailable (private browsing, etc.)
    }
  }

  #getSeekableEnd(): number | undefined {
    const seekable = this.mediaSeekable as
      | [number, number]
      | TimeRanges
      | undefined;
    if (Array.isArray(seekable)) {
      const seekableEnd = seekable[1];
      return Number.isFinite(seekableEnd) ? seekableEnd : undefined;
    }
    if (
      seekable &&
      typeof seekable.length === 'number' &&
      seekable.length > 0 &&
      typeof seekable.end === 'function'
    ) {
      const seekableEnd = seekable.end(seekable.length - 1);
      return Number.isFinite(seekableEnd) ? seekableEnd : undefined;
    }
    return undefined;
  }

  #getEndTime(): number {
    if (Number.isFinite(this.mediaDuration)) {
      return this.mediaDuration;
    }
    const seekableEnd = this.#getSeekableEnd();
    if (typeof seekableEnd === 'number') {
      return seekableEnd;
    }
    return 0;
  }

  override update() {
    if (!this.hasAttribute(DURATION_ONLY_ATTR)) {
      super.update();
      return;
    }

    const slot = this.shadowRoot?.querySelector('slot');
    if (!slot) return;

    const durationLabel = formatTime(this.#getEndTime());
    if (slot.textContent !== durationLabel) {
      slot.textContent = durationLabel;
    }
    this.setAttribute('aria-valuetext', durationLabel);
  }

  /** Save preference after Media Chrome toggles the attribute */
  #handleClick = () => {
    requestAnimationFrame(() => {
      if (
        this.hasAttribute('notoggle') || this.hasAttribute(DURATION_ONLY_ATTR)
      ) {
        this.removeAttribute('remaining');
        return;
      }

      try {
        const isRemaining = this.hasAttribute('remaining');
        localStorage.setItem(STORAGE_KEY, String(isRemaining));
      } catch {
        // localStorage may be unavailable
      }
    });
  };
}

if (!globalThis.customElements.get('bunny-time-display')) {
  globalThis.customElements.define('bunny-time-display', BunnyTimeDisplay);
}

export { BunnyTimeDisplay };
